import { APP_VERSION, BUY_AIRTIME_EVENTS, BUY_BUNDLE_EVENTS, BUY_GOODS_EVENTS, COMPATIBILITY_EVENTS, eventCategory, NETWORK_EVENTS, SEND_MONEY_EVENTS, TECH_EVENTS, WITHDRAW_CASH_EVENTS } from "../data/events";
import { store } from "../Redux";
import { sendGA4Event } from "../services/ga4";
import { getIsGuestUser, getNetworkType } from "../utils/commonUtils";
import packageJson from "../../package.json";
import Bowser from "bowser";
import { ICompatibility, IPaymentOptionsLocData } from "../config/commonInterfaces";
import { AmEventMap } from "../features/BuyBundleV2/interface";
import { FAILED, FINISH, SUCCESS } from "../data/constants";
import { sendEvent } from "../services/eventService";
import { EVENT_TYPES } from "../data/globals";
import { isFromBuyBundle } from "./commonHelpers";

export function minBalanceEventHelper() {
  const balance = store.getState()?.balance?.balance ?? {};
  if (!balance.refreshGsmBal) {
    const minBalanceChecks = [
      { value: balance?.airtimeBal?.total, attribute: TECH_EVENTS.TE_low_airtime_balance },
      { value: balance?.dataBal?.value, attribute: TECH_EVENTS.TE_low_data_balance },
      { value: balance?.smsBal?.value, attribute: TECH_EVENTS.TE_low_sms_balance },
      { value: balance?.voiceBal?.offnet?.value, attribute: TECH_EVENTS.TE_low_voice_offnet_balance },
      { value: balance?.voiceBal?.onnet?.value, attribute: TECH_EVENTS.TE_low_voice_onnet_balance }
    ];

    minBalanceChecks.forEach(({ value, attribute }) => {
      if (!value || value == '0') {
        sendGA4Event({ category: eventCategory.TECH_EVENT, action: attribute });
      }
    });
  }
}

export function minAMBalanceEventHelper(value: number) {
  if (!value) {
    sendGA4Event({ category: eventCategory.TECH_EVENT, action: TECH_EVENTS.TE_low_am_balance });
  }
}

export function sendTransactionLimitLinkEvent() {
  sendGA4Event({ category: eventCategory.TECH_EVENT, action: TECH_EVENTS.TE_TRANSACTION_LIMIT_ERR_LINK_CLICKED });
}

export function sendNetworkTypeEvent() {
  let networkType = getNetworkType();
  sendGA4Event({ category: eventCategory.NETWORK_TYPE, action: NETWORK_EVENTS.network_check_init, label: NETWORK_EVENTS.network_check_init });
  if (networkType)
    sendGA4Event({ category: eventCategory.NETWORK_TYPE, action: `${NETWORK_EVENTS.network_type}_${networkType}`, label: networkType })
  else
    sendGA4Event({ category: eventCategory.NETWORK_TYPE, action: NETWORK_EVENTS.network_check_fail, label: NETWORK_EVENTS.network_check_fail })
  sendGA4Event({ category: eventCategory.NETWORK_TYPE, action: NETWORK_EVENTS.network_check_finish, label: NETWORK_EVENTS.network_check_finish });
}

export function sendAppVersionEvent() {
  let version = packageJson.version;
  sendGA4Event({ category: eventCategory.APP_INFO, action: `${APP_VERSION}_${version}`, label: version });
}

export function sendCompatibilityEvents(eventType: ICompatibility["eventType"] = "") {
  const bowser = Bowser.getParser(window.navigator.userAgent);
  const browserName = bowser.getBrowserName();
  const browserVersion = bowser.getBrowserVersion();

  const resolutionLabel = `${window.screen.width}x${window.screen.height}`;
  const successEvenList = [
    COMPATIBILITY_EVENTS.compatibility_check_resolution_pass,
    COMPATIBILITY_EVENTS.compatibility_check_browser_pass,
    COMPATIBILITY_EVENTS.compatibility_check_browser_ver_pass
  ];
  const eventsMap = {
    resolutionFailed: {
      failAction: COMPATIBILITY_EVENTS.compatibility_check_resolution_fail,
      successAction: [],
      label: resolutionLabel,
    },
    browserNameFailed: {
      failAction: COMPATIBILITY_EVENTS.compatibility_check_browser_fail,
      successAction: [successEvenList[0]],
      label: browserName,
    },
    browserVersionFailed: {
      failAction: COMPATIBILITY_EVENTS.compatibility_check_browser_ver_fail,
      successAction: [successEvenList[0], successEvenList[1]],
      label: browserVersion,
    }
  };

  const event = eventType ? eventsMap[eventType] : "";
  if (event) {
    sendGA4Event({ category: eventCategory.COMPATIBILITY, action: event.failAction, label: event.label });
    sendGA4Event({ category: eventCategory.COMPATIBILITY, action: COMPATIBILITY_EVENTS.compatibility_check_failed });
    if (event.successAction.length) {
      event.successAction.forEach(action => {
        sendGA4Event({ category: eventCategory.COMPATIBILITY, action, label: event.label });
      });
    }
  } else {
    successEvenList.forEach(action => {
      sendGA4Event({ category: eventCategory.COMPATIBILITY, action, label: action });
    });
  }
  sendGA4Event({ category: eventCategory.COMPATIBILITY, action: COMPATIBILITY_EVENTS.compatibility_check_finish });
}

export function sendBuyAirtimeEvent(eventType: 'init' | 'success' | 'failed' | 'finish') {
  const isGuestUser = getIsGuestUser();
  const eventMap = {
    init: isGuestUser ? BUY_AIRTIME_EVENTS.BUY_AIRTIME_GUEST_INIT : BUY_AIRTIME_EVENTS.BUY_AIRTIME_INIT,
    finish: isGuestUser ? BUY_AIRTIME_EVENTS.BUY_AIRTIME_GUEST_FINISH : BUY_AIRTIME_EVENTS.BUY_AIRTIME_FINISH,
    success: isGuestUser ? BUY_AIRTIME_EVENTS.BUY_AIRTIME_GUEST_SUCCESS : BUY_AIRTIME_EVENTS.BUY_AIRTIME_SUCCESS,
    failed: isGuestUser ? BUY_AIRTIME_EVENTS.BUY_AIRTIME_GUEST_FAILED : BUY_AIRTIME_EVENTS.BUY_AIRTIME_FAILED,
  };

  const action = eventMap[eventType];

  sendGA4Event({
    category: eventCategory.BUY_AIRTIME,
    action: action,
    label: action
  });
  // Send finish event after success or failed event
  if (eventType === SUCCESS || eventType === FAILED) {
    const finishAction = eventMap[FINISH];
    sendGA4Event({
      category: eventCategory.BUY_AIRTIME,
      action: finishAction,
      label: action
    });
  }
}

export function sendBuyBundleEvent(eventType: 'init' | 'success' | 'failed' | 'finish') {
  const isGuestUser = getIsGuestUser();
  const eventMap = {
    init: isGuestUser ? BUY_BUNDLE_EVENTS.BUY_BUNDLE_GUEST_INIT : BUY_BUNDLE_EVENTS.BUY_BUNDLE_INIT,
    finish: isGuestUser ? BUY_BUNDLE_EVENTS.BUY_BUNDLE_GUEST_FINISH : BUY_BUNDLE_EVENTS.BUY_BUNDLE_FINISH,
    success: isGuestUser ? BUY_BUNDLE_EVENTS.BUY_BUNDLE_GUEST_SUCCESS : BUY_BUNDLE_EVENTS.BUY_BUNDLE_SUCCESS,
    failed: isGuestUser ? BUY_BUNDLE_EVENTS.BUY_BUNDLE_GUEST_FAILED : BUY_BUNDLE_EVENTS.BUY_BUNDLE_FAILED,
  };

  const action = eventMap[eventType];

  sendGA4Event({
    category: eventCategory.BUY_BUNDLE,
    action: action,
    label: action
  });
  // Send finish event after success or failed event
  if (eventType === SUCCESS || eventType === FAILED) {
    const finishAction = eventMap[FINISH];
    sendGA4Event({
      category: eventCategory.BUY_BUNDLE,
      action: finishAction,
      label: action
    });
  }
}

export function sendAmEvents(from: string, eventType: 'init' | 'success' | 'failed' | 'finish') {
  let eventMap: AmEventMap = getAmEventMap(from);
  const action = eventMap[eventType];

  sendGA4Event({
    category: from,
    action: action ?? '',
    label: action
  });
  // Send finish event after success or failed event
  if (eventType === SUCCESS || eventType === FAILED) {
    const finishAction = eventMap[FINISH];
    sendGA4Event({
      category: from,
      action: finishAction ?? '',
      label: action
    });
  }

}

export function getAmEventMap(type: string) {
  let eventMap: AmEventMap = {};
  switch (type) {
    case eventCategory.SEND_MONEY:
      eventMap = {
        init: SEND_MONEY_EVENTS.SEND_MONEY_INIT,
        success: SEND_MONEY_EVENTS.SEND_MONEY_SUCCESS,
        failed: SEND_MONEY_EVENTS.SEND_MONEY_FAILED,
        finish: SEND_MONEY_EVENTS.SEND_MONEY_FINISH,
      }
      break;
    case eventCategory.WITHDRAW_CASH:
      eventMap = {
        init: WITHDRAW_CASH_EVENTS.WITHDRAW_CASH_INIT,
        success: WITHDRAW_CASH_EVENTS.WITHDRAW_CASH_SUCCESS,
        failed: WITHDRAW_CASH_EVENTS.WITHDRAW_CASH_FAILED,
        finish: WITHDRAW_CASH_EVENTS.WITHDRAW_CASH_FINISH,
      }
      break;
    case eventCategory.BUY_GOODS:
      eventMap = {
        init: BUY_GOODS_EVENTS.BUY_GOODS_INIT,
        success: BUY_GOODS_EVENTS.BUY_GOODS_SUCCESS,
        failed: BUY_GOODS_EVENTS.BUY_GOODS_FAILED,
        finish: BUY_GOODS_EVENTS.BUY_GOODS_FINISH,
      }
      break;
  }
  return eventMap;
}

interface ILogAnalyticsEvent {
  category: string;
  action: string;
  status?: string;
  reason?: string;
  errorMessage?: string;
  target?: string[];
  data?: any
}

export const logAnalyticEvents = ({ category, action, status, reason, errorMessage, target=EVENT_TYPES.ALL, data = {} }: ILogAnalyticsEvent) => {
  const eventObj = {
    status: status || "",
    reason: reason || "",
    errorMessage: errorMessage || "",
    ...data
  };
  sendGA4Event({ category, action, target, data: eventObj });
}

export const sendPymtSelEvt = (locData: IPaymentOptionsLocData) => {
  const fromBuyBundle = isFromBuyBundle(locData?.from);
  let category = fromBuyBundle ? BUY_BUNDLE_EVENTS.BUY_BUNDLE : BUY_AIRTIME_EVENTS.BUY_AIRTIME,
  action = fromBuyBundle ? BUY_BUNDLE_EVENTS.PAYMENT_MODE_SELECTED : BUY_AIRTIME_EVENTS.PAYMENT_MODE_SELECTED;
  action = getIsGuestUser() ? `g_${action}` : action;

  sendGA4Event({ category, action: `${action}_any`, target: EVENT_TYPES.ALL });
  sendGA4Event({ category, action: `${action}_am`, target: EVENT_TYPES.ALL });
}